import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

const REL_TYPES = ["friend", "family", "romantic", "professional", "antagonist", "mentor", "rival", "secret"];
const REL_COLORS = {
  friend: "#22C55E",
  family: "#F59E0B",
  romantic: "#EC4899",
  professional: "#0EA5E9",
  antagonist: "#EF4444",
  mentor: "#A78BFA",
  rival: "#F97316",
  secret: "#D4AF37",
};

export default function Relationships() {
  const { projectId } = useParams();
  const [chars, setChars] = React.useState([]);
  const [rels, setRels] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [suggesting, setSuggesting] = React.useState(false);
  const [selectedRel, setSelectedRel] = React.useState(null);
  const [openNew, setOpenNew] = React.useState(false);
  const { updateCredits } = useAuth();

  const load = React.useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        api.get(`/projects/${projectId}/characters`),
        api.get(`/projects/${projectId}/relationships`),
      ]);
      setChars(c.data);
      setRels(r.data);
    } catch (err) {
      logger.warn("relationships: load failed", err);
      toast.error("Could not load");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/relationships/suggest`);
      updateCredits(data.credits);
      toast.success(`${data.suggestions.length} relationship${data.suggestions.length === 1 ? "" : "s"} suggested — review and confirm.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Suggest failed"); }
    finally { setSuggesting(false); }
  };

  const removeRel = async (id) => {
    if (!window.confirm("Delete relationship?")) return;
    await api.delete(`/projects/${projectId}/relationships/${id}`);
    setRels(rels.filter((r) => r.id !== id));
    setSelectedRel(null);
  };

  const confirmRel = async (rel) => {
    await api.patch(`/projects/${projectId}/relationships/${rel.id}`, { type: rel.type, label: rel.label });
    toast.success("Confirmed");
    load();
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Relationship Graph</div>
            <h1 className="font-editorial text-4xl">The web of your story</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setOpenNew(true)} className="border border-[#27272A] hover:border-[#A1A1AA] text-sm px-4 py-2 inline-flex items-center gap-2" data-testid="rel-add-btn">
              <Plus size={14} /> Add
            </button>
            <button onClick={suggest} disabled={suggesting || chars.length < 2}
              className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F59E0B] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
              data-testid="rel-suggest-btn">
              {suggesting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} AI suggest
            </button>
          </div>
        </div>

        {loading ? <div className="text-[#A1A1AA]">Loading…</div> :
          chars.length < 2 ? (
            <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
              <div className="font-editorial text-2xl">Not enough characters yet.</div>
              <p className="text-sm text-[#A1A1AA] mt-2">Relationships need at least two characters. Write more of the screenplay first.</p>
              <Link to={`/app/projects/${projectId}/script`} className="mt-6 inline-block bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium">Open screenplay</Link>
            </div>
          ) : (
            <div className="grid grid-cols-[1fr_320px] gap-6">
              <div className="border border-[#1E1E1E] bg-[#0F0F10] aspect-square relative overflow-hidden" data-testid="rel-graph">
                <ForceGraph
                  characters={chars}
                  relationships={rels}
                  selected={selectedRel}
                  onSelect={setSelectedRel}
                />
              </div>
              <div className="border border-[#1E1E1E] bg-[#141414] p-5">
                <div className="uppercase text-[10px] tracking-widest text-[#D4AF37] mb-4">Legend</div>
                <div className="space-y-2">
                  {REL_TYPES.map((t) => (
                    <div key={t} className="flex items-center gap-2 text-xs">
                      <div className="w-6 h-0.5" style={{ background: REL_COLORS[t] }}></div>
                      <span className="text-[#E4E4E7] capitalize">{t}</span>
                      <span className="text-[#71717A] ml-auto">{rels.filter((r) => r.type === t).length}</span>
                    </div>
                  ))}
                </div>

                {selectedRel && (
                  <div className="mt-6 border-t border-[#1E1E1E] pt-4">
                    <div className="uppercase text-[10px] tracking-widest text-[#D4AF37] mb-2">Selected</div>
                    <RelEditor
                      rel={selectedRel}
                      chars={chars}
                      onSave={(r) => { confirmRel(r); setSelectedRel(null); }}
                      onDelete={() => removeRel(selectedRel.id)}
                    />
                  </div>
                )}

                <div className="mt-6 text-[10px] text-[#71717A] italic">
                  Dashed lines are AI suggestions. Click any line to review and confirm.
                </div>
              </div>
            </div>
          )
        }
      </div>

      {openNew && (
        <NewRelModal
          chars={chars}
          onClose={() => setOpenNew(false)}
          onCreate={async (payload) => {
            await api.post(`/projects/${projectId}/relationships`, { project_id: projectId, ...payload });
            setOpenNew(false);
            load();
            toast.success("Relationship added");
          }}
        />
      )}
    </DashboardLayout>
  );
}

const GRAPH_SIZE = 640;
const GRAPH_CX = GRAPH_SIZE / 2;
const GRAPH_CY = GRAPH_SIZE / 2;
const GRAPH_R = GRAPH_SIZE * 0.38;

function computeRadialLayout(characters) {
  const map = {};
  const total = characters.length || 1;
  for (let i = 0; i < characters.length; i++) {
    const c = characters[i];
    const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
    map[c.id] = {
      x: GRAPH_CX + GRAPH_R * Math.cos(angle),
      y: GRAPH_CY + GRAPH_R * Math.sin(angle),
      name: c.name,
    };
  }
  return map;
}

function ForceGraph({ characters, relationships, selected, onSelect }) {
  const nodePositions = React.useMemo(() => computeRadialLayout(characters), [characters]);

  return (
    <svg viewBox={`0 0 ${GRAPH_SIZE} ${GRAPH_SIZE}`} className="w-full h-full">
      {/* Edges */}
      {relationships.map((r) => {
        const a = nodePositions[r.from_character_id];
        const b = nodePositions[r.to_character_id];
        if (!a || !b) return null;
        const color = REL_COLORS[r.type] || "#71717A";
        const isSel = selected?.id === r.id;
        return (
          <g key={r.id} onClick={() => onSelect(r)} style={{ cursor: "pointer" }} data-testid={`rel-edge-${r.id}`}>
            <line
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={color}
              strokeWidth={isSel ? 3 : 1.5}
              strokeDasharray={r.ai_suggested ? "6 4" : "0"}
              opacity={isSel ? 1 : 0.75}
            />
            {r.label && (
              <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4}
                fill="#A1A1AA" fontSize="9" textAnchor="middle" style={{ pointerEvents: "none" }}>
                {r.label}
              </text>
            )}
          </g>
        );
      })}
      {/* Nodes */}
      {characters.map((c) => {
        const pos = nodePositions[c.id];
        return (
          <g key={c.id} transform={`translate(${pos.x}, ${pos.y})`}>
            <circle r="26" fill="#141414" stroke="#D4AF37" strokeWidth="1.5" />
            <text y="4" fill="#FFFFF0" fontSize="9" textAnchor="middle" style={{ fontFamily: "IBM Plex Sans" }}>
              {c.name.slice(0, 8)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function RelEditor({ rel, chars, onSave, onDelete }) {
  const [local, setLocal] = React.useState(rel);
  React.useEffect(() => setLocal(rel), [rel]);
  const from = chars.find((c) => c.id === local.from_character_id);
  const to = chars.find((c) => c.id === local.to_character_id);
  return (
    <div className="space-y-3 text-xs">
      <div className="text-[#E4E4E7]">
        <span className="font-editorial text-lg">{from?.name}</span>
        <span className="text-[#71717A] mx-2">→</span>
        <span className="font-editorial text-lg">{to?.name}</span>
      </div>
      <label className="block">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Type</div>
        <select value={local.type} onChange={(e) => setLocal({ ...local, type: e.target.value })}
          className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-2 py-1.5"
          data-testid="rel-type-select">
          {REL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Label</div>
        <input value={local.label || ""} onChange={(e) => setLocal({ ...local, label: e.target.value })}
          className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-2 py-1.5" />
      </label>
      {local.ai_suggested && <div className="text-[10px] text-[#D4AF37] uppercase tracking-widest">AI suggestion · confirm to keep</div>}
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(local)} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] py-2 text-xs font-medium" data-testid="rel-save">Confirm</button>
        <button onClick={onDelete} className="border border-[#7F1D1D] text-[#7F1D1D] px-3" data-testid="rel-delete"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

function NewRelModal({ chars, onClose, onCreate }) {
  const [from, setFrom] = React.useState(chars[0]?.id || "");
  const [to, setTo] = React.useState(chars[1]?.id || "");
  const [type, setType] = React.useState("professional");
  const [label, setLabel] = React.useState("");
  return (
    <div className="fixed inset-0 bg-[#0A0A0A]/85 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-md bg-[#141414] border border-[#27272A] p-8" onClick={(e) => e.stopPropagation()}>
        <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-3">New Relationship</div>
        <h2 className="font-editorial text-3xl mb-6">Connect two characters</h2>
        <div className="space-y-4">
          <SF label="From" value={from} onChange={setFrom} options={chars.map((c) => [c.id, c.name])} />
          <SF label="To" value={to} onChange={setTo} options={chars.filter((c) => c.id !== from).map((c) => [c.id, c.name])} />
          <SF label="Type" value={type} onChange={setType} options={REL_TYPES.map((t) => [t, t])} />
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">Label</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-3 py-2" />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="text-sm text-[#A1A1AA] px-4 py-2">Cancel</button>
          <button onClick={() => onCreate({ from_character_id: from, to_character_id: to, type, label })}
            disabled={!from || !to || from === to}
            className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium disabled:opacity-50" data-testid="rel-create-submit">
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function SF({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-3 py-2">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
